/**
 * Card field definitions and values, shared by backend and frontend. Enums and interfaces
 * only; the per-type zod schemas live in `field_type_schemas.ts`.
 *
 * Every config property is required and JSON-clean: `null` marks absence, never
 * `undefined`, because a JSONB round trip drops undefined members.
 */
import type {OrgRole} from '@shared/auth_types';

// ---- Field Types ---- //

export enum FieldType {
   TEXT = 'text',
   NUMBER = 'number',
   DATE = 'date',
   CHECKBOX = 'checkbox',
   /** AMF calls this "list"; a List is a kanban column here. */
   SELECT = 'select',
   /** Reserved: the interfaces exist, create refuses the type. */
   CARD_REF = 'card_ref',
   HEADER = 'header',
   TAB = 'tab',
}

/** Whether a field type holds a value on the card or only shapes the layout. */
export enum FieldTypeKind {
   DATA = 'data',
   STRUCTURAL = 'structural',
}

/** The kind of every field type. Structural types hold no value. */
export const FIELD_TYPE_KIND: Readonly<Record<FieldType, FieldTypeKind>> = {
   [FieldType.TEXT]: FieldTypeKind.DATA,
   [FieldType.NUMBER]: FieldTypeKind.DATA,
   [FieldType.DATE]: FieldTypeKind.DATA,
   [FieldType.CHECKBOX]: FieldTypeKind.DATA,
   [FieldType.SELECT]: FieldTypeKind.DATA,
   [FieldType.CARD_REF]: FieldTypeKind.DATA,
   [FieldType.HEADER]: FieldTypeKind.STRUCTURAL,
   [FieldType.TAB]: FieldTypeKind.STRUCTURAL,
};

// ---- Display and Permissions ---- //

/**
 * Closed badge color set, resolved to real light and dark colors at the rendering edge.
 * Never used as a CSS value.
 */
export enum BadgeColor {
   GRAY = 'gray',
   BLUE = 'blue',
   TEAL = 'teal',
   GREEN = 'green',
   YELLOW = 'yellow',
   ORANGE = 'orange',
   RED = 'red',
   PURPLE = 'purple',
   PINK = 'pink',
}

export enum LabelLayout {
   TOP = 'top',
   LEFT = 'left',
   RIGHT = 'right',
}

/** Cross-cutting presentation carried by every field row, structural ones included. */
export interface IFieldDisplay {
   /** Show the value on the card front. */
   frontShowValue: boolean;
   /** Show the field name beside the value on the card front. */
   frontShowName: boolean;
   /** New cards start with this field hidden. */
   hideOnNewCards: boolean;
   /** Help text to display to users in UI. */
   help: string | null;
   /** Placeholder shown in the field's input when the value is empty. */
   placeholder: string | null;
   /** Card-back width on a 12-column grid, 1 to 12. */
   width: number;
   labelLayout: LabelLayout;
}

/** Minimum org role plus explicit member grants. Stored and edited; nothing reads it. */
export interface IFieldPerms {
   view: OrgRole;
   viewMemberIds: string[];
   edit: OrgRole;
   editMemberIds: string[];
}

// ---- Formulas and Rules ---- //

/** Formula slots every data field carries. Stored and validated, never evaluated. */
export interface IFieldFormulas {
   calc: IFormula | null;
   visibility: IFormula | null;
}

export interface IFormula {
   parts: FormulaPart[];
}

export type FormulaPart =
   | {kind: 'text'; text: string}
   | {kind: 'field'; fieldId: string}
   | {kind: 'card_field'; cardId: string; fieldId: string}
   | {kind: 'list_field'; listId: string; fieldId: string}
   | {kind: 'board_field'; fieldId: string};

/** Conditional badge color. Stored and validated, never evaluated. */
export interface IColorRule<TOp extends string> {
   /** The operation varies based upon field type. */
   op: TOp;
   value: string | number | null;
   color: BadgeColor;
}

/** Conditional badge decoration. Stored and validated, never evaluated. */
export interface IDecorationRule<TOp extends string> {
   op: TOp;
   value: string | number | null;
   decoration: string;
}

// ---- Text ---- //

export enum TextEditorStyle {
   INPUT = 'input',
   TEXTAREA = 'textarea',
}

export enum TextMaskKind {
   NONE = 'none',
   EMAIL = 'email',
   URL = 'url',
   US_PHONE = 'us_phone',
   E164_PHONE = 'e164_phone',
   HOURS_MINUTES = 'hours_minutes',
   CUSTOM = 'custom',
}

/** Comparison a text color or decoration rule applies to a value. */
export enum TextRuleOp {
   EQUALS = 'equals',
   NOT_EQUALS = 'not_equals',
   CONTAINS = 'contains',
   NOT_CONTAINS = 'not_contains',
   STARTS_WITH = 'starts_with',
   ENDS_WITH = 'ends_with',
   IS_EMPTY = 'is_empty',
   IS_NOT_EMPTY = 'is_not_empty',
}

export interface ITextConfig extends IFieldFormulas {
   editor: TextEditorStyle;
   /** Expected input format, used for input hints and display formatting. */
   mask: {
      kind: TextMaskKind;

      /** User-supplied pattern (digits as 9, e.g. "99:99") for kind CUSTOM. Stored only; no formatter reads it yet and the editor does not offer CUSTOM. */
      custom: string | null;
   };
   colorRules: IColorRule<TextRuleOp>[];
   decorationRules: IDecorationRule<TextRuleOp>[];
}

// ---- Number ---- //

/** How a number renders. */
export enum NumberShowAs {
   NUMBER = 'number',
   PROGRESS = 'progress',
   DURATION = 'duration',
   RATING = 'rating',
}

export enum NumberFormat {
   STANDARD = 'standard',
   PLAIN = 'plain',
   SCIENTIFIC = 'scientific',
   ENGINEERING = 'engineering',
   CURRENCY = 'currency',
   ACCOUNTING = 'accounting',
   PERCENT = 'percent',
   PERCENT_100 = 'percent_100',
}

export enum ProgressBarStyle {
   BAR = 'bar',
   ROUNDED = 'rounded',
   SEGMENTED = 'segmented',
}

export enum DurationFormat {
   HOUR_MINUTE = 'hour_minute',
   HOUR_MINUTE_SECOND = 'hour_minute_second',
   HUMAN = 'human',
}

export enum RatingStyle {
   STAR = 'star',
   HEART = 'heart',
   THUMB = 'thumb',
   CIRCLE = 'circle',
   SQUARE = 'square',
}

/** Comparison a number color or decoration rule applies to a value. */
export enum NumberRuleOp {
   EQUALS = 'equals',
   NOT_EQUALS = 'not_equals',
   GREATER_THAN = 'greater_than',
   GREATER_OR_EQUAL = 'greater_or_equal',
   LESS_THAN = 'less_than',
   LESS_OR_EQUAL = 'less_or_equal',
   IS_EMPTY = 'is_empty',
   IS_NOT_EMPTY = 'is_not_empty',
}

export interface INumberConfig extends IFieldFormulas {
   showAs: NumberShowAs;
   format: NumberFormat;
   /** ISO 4217; read only when format is currency or accounting. */
   currency: string | null;
   decimalPlaces: number | null;
   progress: {style: ProgressBarStyle; showNumber: boolean; min: number; max: number} | null;
   duration: {format: DurationFormat} | null;
   rating: {style: RatingStyle; max: number} | null;
   colorRules: IColorRule<NumberRuleOp>[];
   decorationRules: IDecorationRule<NumberRuleOp>[];
}

// ---- Date ---- //

export enum DateFormat {
   LOCAL = 'local',
   FRIENDLY = 'friendly',
   US = 'us',
   EUROPEAN = 'european',
   ISO = 'iso',
}

/** Comparison a date color or decoration rule applies to a value. */
export enum DateRuleOp {
   IS = 'is',
   BEFORE = 'before',
   AFTER = 'after',
   IS_EMPTY = 'is_empty',
   IS_NOT_EMPTY = 'is_not_empty',
}

export interface IDateConfig extends IFieldFormulas {
   format: DateFormat;
   /** Values are ISO datetimes when true, YYYY-MM-DD when false. */
   includeTime: boolean;
   colorRules: IColorRule<DateRuleOp>[];
   decorationRules: IDecorationRule<DateRuleOp>[];
}

// ---- Checkbox ---- //

export enum CheckboxStyle {
   CHECK = 'check',
   SQUARE = 'square',
   TOGGLE = 'toggle',
   STAR = 'star',
   FLAG = 'flag',
   THUMB = 'thumb',
   CIRCLE = 'circle',
}

export interface ICheckboxConfig extends IFieldFormulas {
   style: CheckboxStyle;
   onColor: BadgeColor | null;
   offColor: BadgeColor | null;
}

// ---- Select ---- //

export enum SelectDisplayMode {
   DROPDOWN = 'dropdown',
   TOGGLE = 'toggle',
   CHIPS = 'chips',
}

export enum SelectFrontStyle {
   SINGLE_BADGE = 'single_badge',
   MULTI_BADGE = 'multi_badge',
}

/** One choice on a select field. Values store ids, never labels. */
export interface ISelectOption {
   /** Server-assigned. */
   id: string;
   label: string;
   color: BadgeColor | null;
   /** Soft retire: hidden from pickers, still resolves for values already stored. */
   archived: boolean;
}

export interface ISelectConfig extends IFieldFormulas {
   options: ISelectOption[];
   multi: boolean;
   frontStyle: SelectFrontStyle;
   displayMode: SelectDisplayMode;
   /** Color for options that have no color of their own. */
   optionFallbackColor: BadgeColor | null;
}

// ---- Card Reference ---- //

export enum CardRefDisplay {
   COMPACT = 'compact',
   FULL = 'full',
}

/** Reserved. The shape exists so a future value round-trips; create refuses the type. */
export interface ICardRefConfig extends IFieldFormulas {
   scope: {boardId: string | null; listIds: string[]} | null;
   multi: boolean;
   cardDisplay: CardRefDisplay;
}

// ---- Structural ---- //

/** Structural. Width and hiding come from IFieldDisplay like every other field. */
export interface IHeaderConfig {
   color: BadgeColor | null;
}

/** Structural. A tab has no settings; the shape exists so every type has a config. */
export interface ITabConfig {
   /** Always null; exists so the config shape survives serialization. */
   settings: null;
}

export type FieldConfig =
   | ITextConfig
   | INumberConfig
   | IDateConfig
   | ICheckboxConfig
   | ISelectConfig
   | ICardRefConfig
   | IHeaderConfig
   | ITabConfig;

// ---- Values ---- //

export type FieldValue = string | number | boolean | string[] | null;

/** A card's field values keyed by board_field id. Absent key = never set; null = cleared. */
export interface ICardFieldValues {
   [fieldId: string]: FieldValue;
}

/**
 * What a field definition carries to validate, render, and (in a future iteration)
 * permission-gate one value — the definition minus its persistence facts (board, rank,
 * timestamps). A persisted IBoardField satisfies it, and so do the editor's in-progress
 * draft and a catalog preset.
 */
export interface IFieldDefinition {
   /** The id of the field definition. */
   id: string;

   /** The type of this field. */
   type: FieldType;

   /** The name of the field. */
   name: string;

   /** Common display settings for the field. */
   display: IFieldDisplay;

   /** Who can see the field and edit its value. Stored now; the client reads it when enforcement lands. */
   perms: IFieldPerms;

   /** The field type specific config. */
   config: FieldConfig;
}

// ---- Config Narrowing ---- //

/**
 * Get the config as the shape its type implies, or null when the type does not match.
 * The row's `type` is the discriminator; the union carries none, so this is the one
 * place a config is cast.
 */
function configForType<CONFIG_T extends FieldConfig>(
   expectedType: FieldType,
   type: FieldType,
   config: FieldConfig,
): CONFIG_T | null {
   return type === expectedType ? (config as CONFIG_T) : null;
}

/** Get the config as a text config, or null when the field is not a text field. */
export function asTextConfig(type: FieldType, config: FieldConfig): ITextConfig | null {
   return configForType<ITextConfig>(FieldType.TEXT, type, config);
}

/** Get the config as a number config, or null when the field is not a number field. */
export function asNumberConfig(type: FieldType, config: FieldConfig): INumberConfig | null {
   return configForType<INumberConfig>(FieldType.NUMBER, type, config);
}

/** Get the config as a date config, or null when the field is not a date field. */
export function asDateConfig(type: FieldType, config: FieldConfig): IDateConfig | null {
   return configForType<IDateConfig>(FieldType.DATE, type, config);
}

/** Get the config as a checkbox config, or null when the field is not a checkbox field. */
export function asCheckboxConfig(type: FieldType, config: FieldConfig): ICheckboxConfig | null {
   return configForType<ICheckboxConfig>(FieldType.CHECKBOX, type, config);
}

/** Get the config as a select config, or null when the field is not a select field. */
export function asSelectConfig(type: FieldType, config: FieldConfig): ISelectConfig | null {
   return configForType<ISelectConfig>(FieldType.SELECT, type, config);
}

/** Get the config as a card reference config, or null when the field is not a card reference. */
export function asCardRefConfig(type: FieldType, config: FieldConfig): ICardRefConfig | null {
   return configForType<ICardRefConfig>(FieldType.CARD_REF, type, config);
}

/** Get the config as a header config, or null when the field is not a header. */
export function asHeaderConfig(type: FieldType, config: FieldConfig): IHeaderConfig | null {
   return configForType<IHeaderConfig>(FieldType.HEADER, type, config);
}

/** Get the config as a tab config, or null when the field is not a tab. */
export function asTabConfig(type: FieldType, config: FieldConfig): ITabConfig | null {
   return configForType<ITabConfig>(FieldType.TAB, type, config);
}
